import { useState, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { IoRestaurantOutline } from "react-icons/io5";
import Image1 from "../../assets/Traiteur/1.png";
import Image2 from "../../assets/Traiteur/2.png";
import Image3 from "../../assets/Traiteur/3.png";
import Image4 from "../../assets/Traiteur/4.png";
import Image5 from "../../assets/Traiteur/5.png";

const slides = [
  { src: Image1, caption: "Buffet d'exception" },
  { src: Image2, caption: "Réceptions privées" },
  { src: Image3, caption: "Mariages & Banquets" },
  { src: Image4, caption: "Cocktails & Soirées" },
  { src: Image5, caption: "Gastronomie du terroir" },
];

export default function Traiteur() {
  const [active,    setActive]    = useState(0);
  const [animating, setAnimating] = useState(false);

  const go = useCallback((dir) => {
    if (animating) return;
    setAnimating(true);
    setActive((prev) =>
      dir === "next"
        ? (prev + 1) % slides.length
        : (prev - 1 + slides.length) % slides.length
    );
    setTimeout(() => setAnimating(false), 400);
  }, [animating]);

  return (
    <Page>
      {/* ── Header ── */}
      <PageHeader>
        <Eyebrow>
          <IoRestaurantOutline />
          <span>La Petite Charrue</span>
        </Eyebrow>
        <PageTitle>Service Traiteur</PageTitle>
        <GoldBar />
        <PageDesc>
          Pour vos événements, mariages, banquets et réceptions,<br />
          nous mettons notre savoir-faire gastronomique à votre service.
        </PageDesc>
      </PageHeader>

      {/* ── Gallery ── */}
      <GalleryWrap>
        <MainSlide>
          <SlideImg
            src={slides[active].src}
            alt={slides[active].caption}
            key={active}
          />
          <SlideOverlay>
            <SlideCaption>{slides[active].caption}</SlideCaption>
            <SlideCounter>{active + 1} / {slides.length}</SlideCounter>
          </SlideOverlay>

          <NavBtn $side="left" onClick={() => go("prev")} aria-label="Précédent">
            <FiChevronLeft />
          </NavBtn>
          <NavBtn $side="right" onClick={() => go("next")} aria-label="Suivant">
            <FiChevronRight />
          </NavBtn>
        </MainSlide>

        {/* Thumbnails */}
        <Thumbs>
          {slides.map((s, i) => (
            <Thumb
              key={i}
              $active={i === active}
              onClick={() => { if (!animating) { setActive(i); } }}
            >
              <img src={s.src} alt={s.caption} />
            </Thumb>
          ))}
        </Thumbs>

        {/* Dots */}
        <Dots>
          {slides.map((_, i) => (
            <Dot key={i} $active={i === active} onClick={() => { if (!animating) setActive(i); }} />
          ))}
        </Dots>
      </GalleryWrap>

      {/* ── Contact CTA ── */}
      <CtaBlock>
        <CtaTitle>Organisons votre événement ensemble</CtaTitle>
        <CtaSub>Contactez-nous pour un devis personnalisé</CtaSub>
        <CtaLink href="tel:0384238914">03 84 23 89 14</CtaLink>
      </CtaBlock>
    </Page>
  );
}

/* ─── Keyframes ─── */
const fadeIn = keyframes`
  from { opacity: 0; transform: scale(1.03); }
  to   { opacity: 1; transform: scale(1); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Styled Components ─── */

const Page = styled.div`
  min-height: 100vh;
  background: var(--bg-base);
  padding-top: 72px;
`;

const PageHeader = styled.header`
  text-align: center;
  padding: 5rem 5vw 3.5rem;
  animation: ${fadeUp} 0.7s ease both;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--font-script);
  font-size: 1rem;
  color: var(--primary);
  font-style: italic;
  letter-spacing: 0.1em;
  margin-bottom: 0.75rem;

  svg { font-size: 1rem; }
`;

const PageTitle = styled.h1`
  font-family: var(--font-heading);
  font-size: clamp(2rem, 5vw, 3.5rem);
  color: var(--cream);
  font-weight: 500;
  letter-spacing: 0.04em;
`;

const GoldBar = styled.div`
  width: 60px;
  height: 2px;
  background: linear-gradient(to right, transparent, var(--primary), transparent);
  margin: 1rem auto;
`;

const PageDesc = styled.p`
  font-size: clamp(0.95rem, 2vw, 1.1rem);
  color: var(--cream-dim);
  line-height: 1.9;
  max-width: 600px;
  margin: 0 auto;
`;

const GalleryWrap = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 0 5vw 3rem;
  animation: ${fadeUp} 0.7s ease 0.15s both;
`;

const MainSlide = styled.div`
  position: relative;
  border-radius: var(--radius-lg);
  overflow: hidden;
  aspect-ratio: 16 / 9;
  background: var(--bg-card);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-lg);
`;

const SlideImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  animation: ${fadeIn} 0.4s ease both;
`;

const SlideOverlay = styled.div`
  position: absolute;
  bottom: 0; left: 0; right: 0;
  padding: 2rem 1.75rem 1.25rem;
  background: linear-gradient(to top, rgba(10,6,4,0.88), transparent);
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`;

const SlideCaption = styled.span`
  font-family: var(--font-heading);
  font-size: clamp(1rem, 2.5vw, 1.4rem);
  color: var(--cream);
  font-style: italic;
`;

const SlideCounter = styled.span`
  font-size: 0.8rem;
  color: var(--primary);
  font-weight: 700;
  letter-spacing: 0.08em;
`;

const NavBtn = styled.button`
  position: absolute;
  top: 50%;
  ${({ $side }) => $side}: 1rem;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  background: rgba(10, 6, 4, 0.7);
  border: 1px solid var(--border);
  border-radius: 50%;
  color: var(--cream);
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--transition);
  backdrop-filter: blur(4px);

  &:hover {
    background: var(--primary);
    color: var(--bg-base);
    border-color: var(--primary);
  }
`;

const Thumbs = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-top: 1rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }

  @media (max-width: 600px) { display: none; }
`;

const Thumb = styled.button`
  flex-shrink: 0;
  width: 80px;
  height: 56px;
  border-radius: var(--radius-sm);
  overflow: hidden;
  border: 2px solid ${({ $active }) => ($active ? "var(--primary)" : "transparent")};
  cursor: pointer;
  padding: 0;
  background: none;
  transition: border-color 0.25s, transform 0.25s;
  opacity: ${({ $active }) => ($active ? 1 : 0.6)};

  &:hover { opacity: 1; transform: translateY(-2px); }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const Dots = styled.div`
  display: none;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;

  @media (max-width: 600px) { display: flex; }
`;

const Dot = styled.button`
  width: ${({ $active }) => ($active ? "20px" : "8px")};
  height: 8px;
  border-radius: 4px;
  background: ${({ $active }) => ($active ? "var(--primary)" : "var(--cream-muted)")};
  border: none;
  cursor: pointer;
  padding: 0;
  transition: all 0.3s ease;
`;

const CtaBlock = styled.div`
  background: var(--bg-surface);
  border-top: 1px solid var(--border);
  text-align: center;
  padding: 4rem 5vw 5rem;
  animation: ${fadeUp} 0.7s ease 0.3s both;
`;

const CtaTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: clamp(1.5rem, 3.5vw, 2.2rem);
  color: var(--cream);
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const CtaSub = styled.p`
  font-size: 1rem;
  color: var(--cream-dim);
  margin-bottom: 1.75rem;
`;

const CtaLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 2.5rem;
  background: linear-gradient(135deg, var(--primary), var(--primary-dark));
  color: var(--bg-base);
  font-family: var(--font-body);
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border-radius: var(--radius-sm);
  text-decoration: none;
  transition: var(--transition);

  &:hover {
    background: linear-gradient(135deg, var(--primary-light), var(--primary));
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(200, 151, 62, 0.35);
    color: var(--bg-base);
  }
`;
